import { adminPageMayFetch } from '../../utils/adminGate'
import type { AdminGate } from '../../utils/adminGate'
import type { ErrorKind } from '../../utils/format'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, relativeTime, whenLabel } from '../../utils/format'
import { isApiError } from '../../services/apiError'
import type {
  ArchivedReportGrant, GovernanceFinding, GovernanceGrantKind, GovernanceOverview, GrantExpiry,
  ScheduleIdentityReason,
} from '../../types/governance'
import { GOVERNANCE_FINDINGS } from '../../types/governance'

/**
 * Pure helpers for the Governance Queue page and its Overview preview.
 *
 * Everything that DECIDES what a reader sees — which state a section is in, what a
 * null prints, how a lapse is worded — lives here rather than in the components,
 * so it is tested without a DOM. Imports only modules that are themselves safe
 * under vitest (no services/api.ts, which reaches MSAL).
 */

// ---------------------------------------------------------------- time

/**
 * An instant the governance endpoint served, as an ISO string that parses as UTC.
 *
 * Every timestamp on this surface is a C# `DateTime` written as UTC that SQL Server
 * hands back with no kind, so the wire carries no offset (`2026-09-01T12:00:00`)
 * and `Date.parse` would read it as LOCAL time — hours out, west of UTC, on a
 * surface whose whole point is "how long ago did this lapse". A value that already
 * carries `Z` or an offset is returned unchanged.
 */
export function utcIso(iso: string): string {
  const trimmed = iso.trim()
  if (/(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) return trimmed
  // A date with no time part parses as UTC already; only a date-time needs the suffix.
  return /T\d{2}:\d{2}/.test(trimmed) ? `${trimmed}Z` : trimmed
}

/** `utcIso`, passing a null through — a timestamp nobody recorded stays unrecorded. */
export function utcIsoOrNull(iso: string | null): string | null {
  return iso == null ? null : utcIso(iso)
}

/** The absolute-plus-relative label for a served instant, or NOT_MEASURED. */
export function whenUtc(iso: string | null): string {
  return whenLabel(utcIsoOrNull(iso))
}

/**
 * How long ago a lapsed grant stopped conferring anything: "lapsed 5h ago".
 *
 * ALWAYS FROM `expiresAt`, never from `daysUntilExpiry`. That field is truncated
 * toward zero (controller WholeDaysUntil), so every grant that lapsed within the
 * last day reads 0 — the sign that is meant to say "already gone" is not there.
 * A future instant can only mean the browser clock is behind the server's; it is
 * printed as the absolute instant rather than as "lapsed in 3m".
 */
export function lapseText(expiresAt: string): string {
  const iso = utcIso(expiresAt)
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return NOT_MEASURED
  // Beyond relativeTime's ±45s "just now" band, a future instant is clock skew.
  if (at - Date.now() > 45_000) return `lapsed at ${new Date(at).toLocaleString()}`
  return `lapsed ${relativeTime(iso)}`
}

/**
 * When a grant nearing expiry stops conferring access: "expires in 3d".
 *
 * Judged against the browser's clock at render time, so a grant that lapsed after
 * the list was fetched says so rather than promising time it no longer has.
 */
export function expiryText(expiresAt: string): string {
  const iso = utcIso(expiresAt)
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return NOT_MEASURED
  if (at <= Date.now()) return lapseText(expiresAt)
  return `expires ${relativeTime(iso)}`
}

/** A report's last run. Null means never — `SavedReport.LastRunAt` survives every prune. */
export function lastRunLabel(lastRunAt: string | null): string {
  return lastRunAt == null ? 'Never' : whenUtc(lastRunAt)
}

// ---------------------------------------------------------------- cells

/** What an unfiled report's workspace cell says. A null WorkspaceId IS how personal reports are stored. */
export const PERSONAL_NOT_FILED = 'Personal (not filed)'

/** The badge beside a workspace that is somebody's personal one. */
export const PERSONAL_WORKSPACE_BADGE = 'personal workspace'

/**
 * The workspace cell of a report row.
 *
 * A null `workspaceId` renders "Personal (not filed)", never "Not filed" or a dash:
 * that is how Cartographer stores a personal report (WorkspaceStore), and
 * `isPersonalWorkspace` is false for it by design, because it has no workspace to
 * be personal. A filed report whose workspace name did not resolve is a dash — an
 * unresolved reference, not a workspace called nothing.
 */
export function workspaceCell(row: {
  workspaceId: string | null
  workspaceName: string | null
  isPersonalWorkspace: boolean
}): { text: string; personalBadge: boolean } {
  if (row.workspaceId == null) return { text: PERSONAL_NOT_FILED, personalBadge: false }
  return { text: row.workspaceName ?? NOT_MEASURED, personalBadge: row.isPersonalWorkspace }
}

/**
 * The "Report or workspace" cell of a grant row.
 *
 * A report grant names its report (and where that report is filed); a membership
 * names its workspace, because it reaches every report in it. A report grant whose
 * report did not resolve says so — its workspace is then unknown, which is not the
 * same as "Personal (not filed)".
 */
export function grantTargetCell(grant: GrantExpiry): {
  primary: string
  secondary: string | null
  personalBadge: boolean
  unresolved: boolean
} {
  if (grant.grantKind === 'WorkspaceMembership') {
    const cell = workspaceCell(grant)
    return { primary: cell.text, secondary: null, personalBadge: cell.personalBadge, unresolved: false }
  }
  if (grant.reportName == null) {
    return { primary: 'Unresolved report', secondary: NOT_MEASURED, personalBadge: false, unresolved: true }
  }
  const cell = workspaceCell(grant)
  return { primary: grant.reportName, secondary: cell.text, personalBadge: cell.personalBadge, unresolved: false }
}

/** Which mechanism a grant row is — the broader one says how broad. */
export function grantKindLabel(kind: GovernanceGrantKind | string): string {
  if (kind === 'ReportPermission') return 'Report grant'
  if (kind === 'WorkspaceMembership') return 'Workspace membership — every report in it'
  return kind
}

/** A principal's stamped name, or their opaque id when none was stamped. */
export function principalLabel(displayName: string | null, id: string): string {
  const name = displayName?.trim()
  return name ? name : id
}

/** Who made a grant: the stamped name, else the id, else a dash. */
export function grantedByLabel(grant: Pick<GrantExpiry, 'grantedByDisplayName' | 'grantedBy'>): string {
  const name = grant.grantedByDisplayName?.trim()
  if (name) return name
  return grant.grantedBy ?? NOT_MEASURED
}

/**
 * The business-owner cell. Null IS the finding here — the orphan rule tests this
 * column — so it reads "None", not a dash that would suggest it was not measured.
 */
export function businessOwnerLabel(principalId: string | null): string {
  return principalId == null ? 'None' : principalId
}

/**
 * A schedule's recipient count. Null means the stored list could not be READ,
 * which is the opposite finding from "delivers to nobody" — so it is a dash with
 * the word, and a measured zero prints 0.
 */
export function recipientLabel(count: number | null): string {
  return count == null ? `${NOT_MEASURED} unreadable` : String(count)
}

// ---------------------------------------------------------------- groups, and archived reports

/**
 * What every group-or-role figure is captioned with. The served
 * `groupOrRoleGrantsCaption` is the full sentence and starts with exactly this;
 * this is the short form a column header or a tile can carry.
 */
export const GROUP_MEMBERS_NOT_RESOLVABLE = 'group members not resolvable'

/** The badge on a grant whose report somebody archived. */
export const REPORT_ARCHIVED_BADGE = 'report archived'

/**
 * The served caption for group-or-role figures, or the short form when an API
 * predating the field sent none — never nothing, because an unexplained group
 * count reads as people.
 */
export function groupOrRoleCaption(data: Pick<GovernanceOverview, 'groupOrRoleGrantsCaption'> | undefined): string {
  const served = data?.groupOrRoleGrantsCaption?.trim()
  return served ? served : GROUP_MEMBERS_NOT_RESOLVABLE
}

/**
 * What a grant on an archived report confers, in one line.
 *
 * NOT "latent": an archived report keeps CanView for its grantees, definition
 * included, so the access is view-only NOW; run, export and schedule are what
 * return on restore. A lapsed grant returns nothing. A Group or Role grant confers
 * nothing before or after a restore — its members are not resolvable — so it is
 * said, rather than called view-only.
 */
export function archivedGrantState(
  grant: Pick<ArchivedReportGrant, 'isLapsed' | 'principalType'>,
): string {
  if (grant.isLapsed) return 'Lapsed — would not return on restore'
  if (grant.principalType !== 'User') return `Confers nothing — ${GROUP_MEMBERS_NOT_RESOLVABLE}`
  return 'View-only now; run, export and schedule return on restore'
}

/**
 * The badge a grant's REPORT status earns in the expiry tables: none for an Active
 * report (or a membership, which has no report), the status in words otherwise —
 * so a grant on a Delisted, Retired or Deleted report is marked, never hidden. The
 * page states the status the server served and draws no runnable verdict from it.
 */
export function grantReportStatusBadge(status: string | null): string | null {
  if (status == null || status === 'Active') return null
  if (status === 'Archived') return REPORT_ARCHIVED_BADGE
  const words = status.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return `report ${words}`
}

/**
 * The reach section's subtitle: reports shared with PEOPLE, out of those examined,
 * and — only when there are any — how many more carry nothing but group or role
 * grants. The people figure leads because a report whose only claims are group
 * rows is shared with nobody who can open it.
 */
export function reachSubtitle(counts: {
  withPeople: number | null | undefined
  onlyGroupOrRole: number | null | undefined
  examined: number | null | undefined
}): string {
  const head = `${countOrDash(counts.withPeople)} of ${countOrDash(counts.examined)} reports examined are shared with people.`
  const groups = counts.onlyGroupOrRole != null && counts.onlyGroupOrRole > 0
    ? ` ${exactNumber(counts.onlyGroupOrRole)} more carry only group or role grants — ${GROUP_MEMBERS_NOT_RESOLVABLE}, so they reach nobody.`
    : ''
  return `${head}${groups} People granted and workspace members are side by side and never added: one person can hold both.`
}

/** Why a schedule was listed, in words. An unknown reason prints as served. */
export function scheduleReasonLabel(reason: ScheduleIdentityReason | string): string {
  switch (reason) {
    case 'RunAsDiffersFromOwner': return 'Runs as someone other than the owner'
    case 'NoBusinessOwner': return 'Report has no business owner'
    case 'ReportNeedsAttention': return 'Report flagged for attention'
    default: return reason
  }
}

// ---------------------------------------------------------------- counts

/**
 * The count beside a finding: "showing 100 of 412" when the list is a capped
 * sample, the count alone when it is the whole of it, a dash when not measured.
 *
 * Every list is capped at `maxRowsPerFinding`; presenting a truncated list as the
 * whole of a finding is the same lie as a measured zero, one level up.
 */
export function findingTally(shown: number, count: number | null): string {
  if (count == null) return NOT_MEASURED
  if (count > shown) return `showing ${exactNumber(shown)} of ${exactNumber(count)}`
  return exactNumber(count)
}

/** A count that may not have been measured. */
export function countOrDash(count: number | null | undefined): string {
  return count == null ? NOT_MEASURED : exactNumber(count)
}

/**
 * The served count for one finding group's tile, or null when this response did
 * not compute it.
 *
 * Reach's tile is the PEOPLE figure, not `sharedReportCount`: a report reachable
 * only through group or role grants reaches nobody, so counting it on a tile
 * labelled "shared" would overclaim. `?? null` because an API predating a field
 * omits it rather than nulling it.
 */
export function findingCount(finding: GovernanceFinding, data: GovernanceOverview | undefined): number | null {
  if (!data) return null
  switch (finding) {
    case 'ownership': return data.ownershipGapCount
    case 'expiring': return data.grantsNearingExpiryCount
    case 'lapsed': return data.lapsedGrantCount
    case 'archived': return data.grantsOnArchivedReportsCount ?? null
    case 'reach': return data.reportsSharedWithPeopleCount ?? null
    case 'schedules': return data.schedulesRunningAsNonOwnerCount
    case 'inactive': return data.inactiveGrantHolderCount
  }
}

// ---------------------------------------------------------------- inactive holders: live apart from view-only

/**
 * How many inactive holders the LIST holds a total for: live holders and view-only-only holders together, because the
 * list carries both (live first). The section's "showing N of M" is over this; the tile is the live count alone. Null
 * when not computed.
 */
export function inactiveListedCount(
  data: Pick<GovernanceOverview, 'inactiveGrantHolderCount' | 'inactiveViewOnlyHolderCount'>,
): number | null {
  if (data.inactiveGrantHolderCount == null) return null
  return data.inactiveGrantHolderCount + (data.inactiveViewOnlyHolderCount ?? 0)
}

/**
 * "4 hold live access; 2 more hold only view-only grants on reports that cannot run — listed, never counted as live."
 * Null when not computed, or when an older API did not split the two.
 */
export function inactiveSplitText(
  data: Pick<GovernanceOverview, 'inactiveGrantHolderCount' | 'inactiveViewOnlyHolderCount'> | undefined,
): string | null {
  if (!data || data.inactiveGrantHolderCount == null || data.inactiveViewOnlyHolderCount == null) return null
  const live = data.inactiveGrantHolderCount
  const viewOnly = data.inactiveViewOnlyHolderCount
  return `${exactNumber(live)} ${live === 1 ? 'holds' : 'hold'} live access; ${exactNumber(viewOnly)} more `
    + `${viewOnly === 1 ? 'holds' : 'hold'} only view-only grants on reports that cannot run — listed, never counted as live.`
}

// ---------------------------------------------------------------- the inactivity window

/**
 * The one top-of-page notice when the inactivity list is withheld, or null when the
 * run log covers the window.
 *
 * Said once, at the top, because the absence of an inactivity finding must not be
 * read as the absence of inactive holders: "nobody is inactive" and "we cannot
 * tell" are opposite answers, and the second is what this declares.
 */
export function inactivityWithheldNotice(
  data: Pick<GovernanceOverview, 'runLogCoversInactivityWindow' | 'runLogReachesBackTo' | 'inactivityThresholdDays'>,
): string | null {
  if (data.runLogCoversInactivityWindow) return null
  if (data.runLogReachesBackTo == null) {
    return 'Inactive grant holders are withheld: nothing has ever run, so the run log covers none of the '
      + `${data.inactivityThresholdDays} days needed.`
  }
  return `Inactive grant holders are withheld: the run log reaches back only to ${whenUtc(data.runLogReachesBackTo)}, `
    + `and ${data.inactivityThresholdDays} days are needed.`
}

/**
 * The first response that arrived, in the order given — the source of the
 * measurements every response carries (examined counts, windows, the run log's
 * reach). Every group's response serves them, so any loaded one will do.
 */
export function measurementSource(
  responses: ReadonlyArray<GovernanceOverview | undefined>,
): GovernanceOverview | undefined {
  return responses.find((response) => response !== undefined)
}

// ---------------------------------------------------------------- states

/**
 * The five groups that read AddGrantExpiryAndNotes' columns (WorkspaceMember.ExpiresAt
 * and the two grant notes), which Staging and Production do not have until a person
 * applies that migration. Archived reads ReportPermission.GrantNote. Ownership and
 * schedules read none of them.
 */
export const MIGRATION_DEPENDENT: readonly GovernanceFinding[] = ['expiring', 'lapsed', 'archived', 'reach', 'inactive']

export function isMigrationDependent(finding: GovernanceFinding): boolean {
  return MIGRATION_DEPENDENT.includes(finding)
}

/** What one finding section shows. Loading, restricted, broken, withheld, empty and rows never look alike. */
export type SectionView<T> =
  | { kind: 'loading' }
  | { kind: 'restricted'; reason: string | null }
  | { kind: 'error'; errorKind: ErrorKind; summary: string | null }
  | { kind: 'not-computed' }
  | { kind: 'empty'; count: number | null }
  | { kind: 'rows'; rows: T[]; count: number | null }

/**
 * Decides a section's state from its query. The ORDER is the contract.
 *
 *  1. An error first, and a 403 is restricted rather than broken.
 *  2. `isLoading || !data` next — between React Query's retries the flags read
 *     "not loading, not errored" with no payload, and a section rendered then
 *     would say "None found" while the request is in fact failing.
 *  3. A null list was not computed (for inactivity: withheld). Never "None found".
 *  4. Only a served, empty list is "None found".
 */
export function sectionView<T>(
  query: { isLoading: boolean; isError: boolean; error: unknown; data: GovernanceOverview | undefined },
  select: (data: GovernanceOverview) => { rows: T[] | null; count: number | null },
): SectionView<T> {
  if (query.isError) {
    const kind = errorKind(query.error)
    if (kind === 'forbidden') {
      return { kind: 'restricted', reason: isApiError(query.error) ? query.error.detail : null }
    }
    return { kind: 'error', errorKind: kind, summary: errorSummary(query.error) }
  }
  if (query.isLoading || !query.data) return { kind: 'loading' }
  const { rows, count } = select(query.data)
  if (rows == null) return { kind: 'not-computed' }
  if (rows.length === 0) return { kind: 'empty', count }
  return { kind: 'rows', rows, count }
}

/** The title and message of a failed section. None of them reads as "queue is clear". */
export function sectionErrorCopy(
  finding: GovernanceFinding,
  sectionName: string,
  kind: ErrorKind,
): { title: string; message: string } {
  const noVerdict = 'This says nothing about whether there are findings here.'

  if (kind === 'server' && isMigrationDependent(finding)) {
    return {
      title: `Could not load ${sectionName} — AddGrantExpiryAndNotes may not be applied`,
      message: 'The API failed while computing this group. The likeliest cause outside local dev is migration '
        + 'AddGrantExpiryAndNotes not having been applied: this group reads the membership expiry and the grant '
        + 'notes it adds, and migrations here are applied by hand and never by the app. An API that is not '
        + `running lands here identically. ${noVerdict}`,
    }
  }
  if (kind === 'server') {
    return {
      title: `Could not load ${sectionName}`,
      message: 'The API failed while computing this group. This group reads no column that a pending '
        + 'migration adds, so the fault is elsewhere — the browser console and the API log distinguish the '
        + `causes, and the dev proxy answers 500 when the API is simply not running. ${noVerdict}`,
    }
  }
  if (kind === 'unauthorized') {
    return {
      title: 'Not signed in',
      message: `The API did not accept this session's credentials. Sign in again, or check the API key. ${noVerdict}`,
    }
  }
  if (kind === 'network') {
    return {
      title: 'Could not reach the API',
      message: `The request never got an answer. ${noVerdict}`,
    }
  }
  if (kind === 'bad-request') {
    return {
      title: `The server refused the ${sectionName} request`,
      message: `This page asked for a finding group the server does not recognise. ${noVerdict}`,
    }
  }
  return {
    title: `Could not load ${sectionName}`,
    message: `The request failed. ${noVerdict}`,
  }
}

// ---------------------------------------------------------------- the gate

/**
 * The page fetches once the gate has answered and not refused: `unknown` must never lock an
 * administrator out, and `pending` (a cold load, before the catalogue answers) must not race the
 * answer — six requests from a denied caller before its "no" arrives is exactly that race.
 */
export function pageFetchEnabled(gate: AdminGate): boolean {
  return adminPageMayFetch(gate)
}

/** A preview or badge fetches ONLY on `admin` — it never polls an endpoint the caller may not read. */
export function previewFetchEnabled(gate: AdminGate): boolean {
  return gate.state === 'admin'
}

/** What the Overview preview shows before any count. */
export type PreviewAccess = 'fetch' | 'restricted' | 'checking' | 'undetermined'

/**
 * `admin` fetches, `denied` is restricted, `pending` is a skeleton while the
 * catalogue is being asked — and `unknown` is "Could not determine access" once
 * it failed or answered without a permission (an API predating the field),
 * because a skeleton that never resolves reads as a page still loading.
 */
export function previewAccess(
  gate: AdminGate,
  catalogue: { isError: boolean; isLoaded: boolean },
): PreviewAccess {
  if (gate.state === 'admin') return 'fetch'
  if (gate.state === 'denied') return 'restricted'
  // `pending` has, by definition, neither a catalogue nor an error, so it lands on 'checking' here.
  return catalogue.isError || catalogue.isLoaded ? 'undetermined' : 'checking'
}

// ---------------------------------------------------------------- deep links

/** The DOM id of a finding's section, which `?finding=<key>` scrolls to. */
export function findingSectionId(finding: GovernanceFinding): string {
  return `governance-finding-${finding}`
}

/** `?finding=` as a finding, or null for anything the page has no section for. */
export function parseFindingParam(value: string | null): GovernanceFinding | null {
  const normalised = value?.trim().toLowerCase()
  return GOVERNANCE_FINDINGS.find((finding) => finding === normalised) ?? null
}

/**
 * Whether `?finding=<key>` may scroll yet: once the target and every section above
 * it have stopped loading. Earlier, the rows arriving above the target push it off
 * the screen the moment the scroll lands.
 */
export function scrollReady(
  focus: GovernanceFinding | null,
  loading: Readonly<Record<GovernanceFinding, boolean>>,
): boolean {
  if (!focus) return false
  const through = GOVERNANCE_FINDINGS.indexOf(focus)
  return GOVERNANCE_FINDINGS.slice(0, through + 1).every((finding) => !loading[finding])
}

/** The page route, optionally deep-linked to one finding. `/governance` is Cartographer's. */
export function governanceQueuePath(finding?: GovernanceFinding): string {
  return finding ? `/governance-queue?finding=${finding}` : '/governance-queue'
}

/** Where revocations are recorded: they are hard deletes, so the audit trail is the only record. */
export const PERMISSION_AUDIT_PATH = '/audit?category=permissions'
